
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  Heart, 
  Sparkles, 
  ArrowRight, 
  Droplets, 
  Activity, 
  Leaf, 
  Wind,
  Zap,
  CheckCircle2
} from 'lucide-react';

const Home: React.FC = () => {
  return (
    <div className="bg-transparent min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:items-center lg:space-x-12">
            <div className="lg:w-1/2 reveal-up">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-100/50 text-teal-700 text-xs font-bold uppercase tracking-widest mb-6 border border-teal-200">
                Premium Wellness Experience
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-slate-900 leading-[1.1] mb-6">
                Redefining <span className="text-teal-600">Well-being</span> for Your Lifestyle
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-10 leading-relaxed max-w-xl">
                Sangeeta Wellness is dedicated to supporting your everyday journey through quality lifestyle products and services rooted in transparency and ethics.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6">
                <Link
                  to="/contact"
                  className="bg-teal-600 text-white px-10 py-5 rounded-2xl text-center font-bold hover:bg-teal-700 transition-all shadow-2xl shadow-teal-600/30 flex items-center justify-center group transform hover:-translate-y-1"
                >
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform" />
                </Link>
                <Link
                  to="/about"
                  className="bg-white/80 backdrop-blur-sm text-slate-700 border border-slate-200 px-10 py-5 rounded-2xl text-center font-bold hover:bg-white hover:border-teal-300 transition-all transform hover:-translate-y-1"
                >
                  Our Philosophy
                </Link>
              </div>
              
              <div className="mt-12 flex items-center space-x-6">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-teal-100 border-2 border-white flex items-center justify-center">
                      <Heart className="w-4 h-4 text-teal-600" />
                    </div>
                  ))}
                </div>
                <p className="text-sm text-slate-500 font-medium">
                  Trusted by individuals across <span className="text-slate-900 font-bold">Madhya Pradesh</span>
                </p>
              </div>
            </div>

            <div className="mt-16 lg:mt-0 lg:w-1/2 relative flex items-center justify-center scale-in">
              <div className="relative w-full aspect-square max-w-lg flex items-center justify-center">
                {/* Visual Icon Garden */}
                <div className="z-10 bg-white/90 backdrop-blur-md p-10 rounded-[3rem] shadow-2xl border border-teal-50 animate-float relative overflow-hidden group">
                  <div className="absolute inset-0 bg-gradient-to-tr from-teal-50/50 to-transparent"></div>
                  <Heart className="h-24 w-24 text-teal-600 relative z-10" />
                </div>

                {/* Satellite Tags */}
                <div className="absolute -top-10 -left-4 bg-white p-4 rounded-2xl shadow-xl border border-teal-50 animate-float" style={{ animationDelay: '0s' }}>
                  <div className="flex items-center space-x-3">
                    <Droplets className="h-6 w-6 text-sky-500" />
                    <span className="text-sm font-bold text-slate-800">Quality Assured</span>
                  </div>
                </div>

                <div className="absolute bottom-4 -right-8 bg-white p-4 rounded-2xl shadow-xl border border-emerald-50 animate-float" style={{ animationDelay: '1.5s' }}>
                  <div className="flex items-center space-x-3">
                    <Leaf className="h-6 w-6 text-emerald-500" />
                    <span className="text-sm font-bold text-slate-800">Ethical Sourcing</span>
                  </div>
                </div>

                <div className="absolute top-20 -right-12 bg-white p-4 rounded-2xl shadow-xl border border-teal-50 animate-float" style={{ animationDelay: '3s' }}>
                  <div className="flex items-center space-x-3">
                    <Sparkles className="h-6 w-6 text-teal-400" />
                    <span className="text-sm font-bold text-slate-800">Premium Life</span>
                  </div>
                </div>

                {/* Mobile visible trust indicator */}
                <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur px-6 py-4 rounded-2xl shadow-xl border border-slate-100 flex items-center space-x-4 lg:hidden w-max">
                  <ShieldCheck className="h-6 w-6 text-teal-600" />
                  <span className="text-sm font-bold">Transparent Business</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white/50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20 reveal-up">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              A Foundation of <span className="text-teal-600">Integrity</span>
            </h2>
            <div className="h-1.5 w-24 bg-teal-600 mx-auto rounded-full mb-8"></div>
            <p className="text-lg text-slate-600 leading-relaxed">
              We focus on enhancing your quality of life through responsible support and carefully selected offerings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                icon: Heart,
                title: "Daily Wellness",
                desc: "Focused on routines that build long-term value and balanced living for everyone.",
                color: "teal"
              },
              {
                icon: ShieldCheck,
                title: "Ethical Practices",
                desc: "We prioritize customer trust and complete transparency in every business interaction.",
                color: "sky"
              },
              {
                icon: CheckCircle2,
                title: "Quality Driven",
                desc: "Our selection process is rigorous, ensuring only the finest solutions reach our clients.",
                color: "emerald"
              }
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="bg-white p-10 rounded-[2.5rem] shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 border border-slate-100 group relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 p-4 opacity-[0.03] group-hover:opacity-10 transition-opacity">
                  <item.icon className="w-32 h-32" />
                </div>
                <div className={`bg-${item.color}-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-teal-600 transition-colors duration-500`}>
                  <item.icon className={`h-7 w-7 text-${item.color}-600 group-hover:text-white transition-colors`} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-slate-900 rounded-[3rem] p-10 md:p-20 text-center text-white relative overflow-hidden shadow-2xl">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-64 h-64 bg-teal-500 rounded-full blur-[120px]"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-sky-500 rounded-full blur-[120px]"></div>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold mb-8 max-w-3xl mx-auto">Enhance Your Daily Living Experience</h2>
              <p className="text-slate-400 mb-12 text-lg max-w-xl mx-auto">
                Discover how Sangeeta Wellness can help you maintain a balanced and mindful lifestyle.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Link
                  to="/contact"
                  className="bg-teal-500 text-white px-10 py-5 rounded-2xl font-bold hover:bg-teal-400 transition-all shadow-xl shadow-teal-500/20 w-full sm:w-auto transform hover:scale-105"
                >
                  Contact Us Now
                </Link>
                <Link
                  to="/about"
                  className="bg-transparent text-white border border-slate-700 px-10 py-5 rounded-2xl font-bold hover:bg-slate-800 transition-all w-full sm:w-auto"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
